import { useState, useEffect } from "react";
import { apiFetch } from "../App";

export default function CLVTracker() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [logForm, setLogForm] = useState({
    matchId: "", homeTeam: "", awayTeam: "", sport: "soccer_epl",
    commenceTime: "", bookmaker: "betway_bw", bookmakeName: "Betway",
    market: "h2h", selection: "", oddsAtPlacement: "", stake: ""
  });
  const [logMsg, setLogMsg] = useState("");
  const [showLog, setShowLog] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const isMobile = width < 768;

  useEffect(() => {
    setLoading(true);
    apiFetch(`/clv?days=${days}`)
      .then(setStats).catch(console.error).finally(() => setLoading(false));
  }, [days]);

  const logBet = async () => {
    try {
      await apiFetch("/clv/bets", {
        method: "POST",
        body: JSON.stringify({
          ...logForm,
          matchId: logForm.matchId || `manual_${Date.now()}`,
          oddsAtPlacement: parseFloat(logForm.oddsAtPlacement),
          stake: parseFloat(logForm.stake),
          commenceTime: new Date(logForm.commenceTime).toISOString(),
        }),
      });
      setLogMsg("✅ Bet logged! CLV will be calculated after the match closes.");
      setShowLog(false);
      setLogForm(f => ({ ...f, selection: "", oddsAtPlacement: "", stake: "", homeTeam: "", awayTeam: "" }));
    } catch (e) {
      setLogMsg(`❌ ${e.message}`);
    }
  };

  const avgClvColor = stats?.avgClv >= 2 ? "var(--success)" : stats?.avgClv >= 0 ? "var(--accent)" : "var(--danger)";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Stats row — 2x2 on mobile */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 }}>
        <div className="stat-card" style={{ "--accent-color": avgClvColor }}>
          <div className="stat-label">Avg CLV ({days}d)</div>
          <div className="stat-value" style={{ color: avgClvColor, fontSize: isMobile ? 20 : 28 }}>
            {loading ? "—" : stats ? `${stats.avgClv >= 0 ? "+" : ""}${stats.avgClv}%` : "—"}
          </div>
          <div className={`stat-change ${stats?.avgClv >= 0 ? "up" : "down"}`}>
            {stats?.avgClv >= 2 ? "Excellent" : stats?.avgClv >= 0 ? "Positive" : "Negative"}
          </div>
        </div>
        <div className="stat-card" style={{ "--accent-color": "var(--accent2)" }}>
          <div className="stat-label">Total Tracked</div>
          <div className="stat-value" style={{ fontSize: isMobile ? 20 : 28 }}>{loading ? "—" : stats?.totalBets ?? 0}</div>
          <div className="stat-change neutral">Bets with CLV data</div>
        </div>
        <div className="stat-card" style={{ "--accent-color": "var(--accent3)" }}>
          <div className="stat-label">Positive CLV Rate</div>
          <div className="stat-value" style={{ fontSize: isMobile ? 20 : 28 }}>{loading ? "—" : `${stats?.positiveClvPct ?? 0}%`}</div>
          <div className="stat-change up">Beat the close</div>
        </div>
        <div className="stat-card" style={{ "--accent-color": "var(--success)" }}>
          <div className="stat-label">Best Sport</div>
          <div className="stat-value" style={{ fontSize: isMobile ? 16 : 20 }}>{loading ? "—" : stats?.bestSport?.key || "—"}</div>
          <div className="stat-change up">{stats?.bestSport ? `+${stats.bestSport.avg.toFixed(1)}% avg` : "Log more bets"}</div>
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {[7, 30, 90].map(d => (
            <button key={d} className={`btn ${days === d ? "btn-primary" : "btn-ghost"}`}
              style={{ fontSize: 12, padding: "6px 14px" }} onClick={() => setDays(d)}>
              {d}d
            </button>
          ))}
        </div>
        <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={() => setShowLog(s => !s)}>
          + Log a bet
        </button>
      </div>

      {/* Log bet form */}
      {showLog && (
        <div className="card" style={{ padding: 20 }}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14, marginBottom: 16 }}>Log a Bet for CLV Tracking</div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div className="input-group">
              <label className="input-label">Home Team</label>
              <input className="input-field" value={logForm.homeTeam} onChange={e => setLogForm(f => ({ ...f, homeTeam: e.target.value }))} placeholder="e.g. Man City" />
            </div>
            <div className="input-group">
              <label className="input-label">Away Team</label>
              <input className="input-field" value={logForm.awayTeam} onChange={e => setLogForm(f => ({ ...f, awayTeam: e.target.value }))} placeholder="e.g. Arsenal" />
            </div>
            <div className="input-group">
              <label className="input-label">Kick-off Date/Time</label>
              <input className="input-field" type="datetime-local" value={logForm.commenceTime} onChange={e => setLogForm(f => ({ ...f, commenceTime: e.target.value }))} />
            </div>
            <div className="input-group">
              <label className="input-label">Selection</label>
              <input className="input-field" value={logForm.selection} onChange={e => setLogForm(f => ({ ...f, selection: e.target.value }))} placeholder="e.g. Man City" />
            </div>
            <div className="input-group">
              <label className="input-label">Odds taken</label>
              <input className="input-field" type="number" step="0.01" value={logForm.oddsAtPlacement} onChange={e => setLogForm(f => ({ ...f, oddsAtPlacement: e.target.value }))} placeholder="e.g. 2.10" />
            </div>
            <div className="input-group">
              <label className="input-label">Stake (BWP)</label>
              <input className="input-field" type="number" value={logForm.stake} onChange={e => setLogForm(f => ({ ...f, stake: e.target.value }))} placeholder="e.g. 50" />
            </div>
            <div className="input-group">
              <label className="input-label">Bookmaker</label>
              <select className="input-field" value={logForm.bookmaker} onChange={e => setLogForm(f => ({ ...f, bookmaker: e.target.value, bookmakeName: e.target.options[e.target.selectedIndex].text }))}>
                <option value="betway_bw">Betway</option>
                <option value="sunbet_bw">Sunbet</option>
                <option value="pstbets_bw">PST Bets</option>
                <option value="bbets_bw">BBets</option>
                <option value="bet267_bw">Bet267</option>
                <option value="betxplosion_bw">BetXplosion</option>
              </select>
            </div>
            <div className="input-group">
              <label className="input-label">Market</label>
              <select className="input-field" value={logForm.market} onChange={e => setLogForm(f => ({ ...f, market: e.target.value }))}>
                <option value="h2h">Match Result (1X2)</option>
                <option value="totals">Totals (Over/Under)</option>
                <option value="spreads">Asian Handicap</option>
                <option value="btts">Both Teams to Score</option>
              </select>
            </div>
          </div>
          {logMsg && <div style={{ fontSize: 13, color: logMsg.startsWith("✅") ? "var(--success)" : "var(--danger)", marginBottom: 12 }}>{logMsg}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-primary" onClick={logBet}>Save Bet</button>
            <button className="btn btn-ghost" onClick={() => setShowLog(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* History table — cards on mobile */}
      <div className="card">
        <div className="section-header">
          <div className="section-title">CLV History</div>
          <div style={{ fontSize: 12, color: "var(--text3)" }}>Closing odds recorded automatically</div>
        </div>
        {loading ? (
          <div style={{ padding: 20 }}>{[...Array(6)].map((_, i) => <div key={i} className="skeleton" style={{ height: 44, marginBottom: 8 }} />)}</div>
        ) : !stats?.history?.length ? (
          <div className="empty-state">
            <div className="empty-icon">📈</div>
            <p>No CLV data yet. Log your first bet above to start tracking.</p>
          </div>
        ) : isMobile ? (
          <div>
            {stats.history.map((b, i) => (
              <div key={i} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.match}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{b.selection} · {b.bookmaker} · {b.openOdds}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: b.clvPct >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                    {b.clvPct != null ? `${b.clvPct >= 0 ? "+" : ""}${b.clvPct}%` : "Pending"}
                  </div>
                  <span className={`pill ${b.clvStatus === "beat" ? "pill-green" : b.clvStatus === "missed" ? "pill-red" : "pill-blue"}`} style={{ fontSize: 10 }}>
                    {b.clvStatus === "beat" ? "✓ Beat" : b.clvStatus === "missed" ? "✗ Missed" : "Pending"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Match</th><th>Selection</th><th>Book</th>
                <th style={{ textAlign: "center" }}>Open</th>
                <th style={{ textAlign: "center" }}>Close</th>
                <th style={{ textAlign: "right" }}>CLV</th>
                <th style={{ textAlign: "right" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {stats.history.map((b, i) => (
                <tr key={i}>
                  <td style={{ fontSize: 12 }}>{b.match}</td>
                  <td style={{ fontSize: 12 }}>{b.selection}</td>
                  <td style={{ fontSize: 11, color: "var(--text3)" }}>{b.bookmaker}</td>
                  <td style={{ textAlign: "center", fontFamily: "var(--font-display)", fontWeight: 600 }}>{b.openOdds}</td>
                  <td style={{ textAlign: "center", fontFamily: "var(--font-display)", color: "var(--text3)" }}>{b.closeOdds ?? "—"}</td>
                  <td style={{ textAlign: "right", fontFamily: "var(--font-display)", fontWeight: 700, color: b.clvPct >= 0 ? "var(--success)" : "var(--danger)" }}>
                    {b.clvPct != null ? `${b.clvPct >= 0 ? "+" : ""}${b.clvPct}%` : "Pending"}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <span className={`pill ${b.clvStatus === "beat" ? "pill-green" : b.clvStatus === "missed" ? "pill-red" : "pill-blue"}`} style={{ fontSize: 10 }}>
                      {b.clvStatus === "beat" ? "✓ Beat" : b.clvStatus === "missed" ? "✗ Missed" : "Pending"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}